import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { 
  FiRefreshCw, 
  FiChevronLeft, 
  FiChevronRight,
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiEye
} from "react-icons/fi";
import Spinner from "./Spinner";
import ModalLeadDetails from "./ModalLeadDetails";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const RecentRequestsSection = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  animation: ${fadeIn} 0.5s ease forwards;
  position: relative;
  overflow: hidden;
  z-index: 1;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%);
    z-index: -1;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.3rem;
    font-weight: 600;
    color: #1a1a1a;
  }
`;

const LeadsTableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-top: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  background: #fff;
  position: relative;
`;

const RealocationLeadsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-radius: 8px;
  overflow: hidden;

  thead {
    background-color: #f8fafc;
    
    tr {
      border-bottom: 2px solid #eee;
    }

    th {
      padding: 1rem;
      text-align: left;
      color: #666;
      font-weight: 600;
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      
      svg {
        margin-right: 0.5rem;
        vertical-align: middle;
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #eee;
      transition: all 0.2s ease;

      &:hover {
        background-color: #f8fafc;
      }

      &:last-child {
        border-bottom: none;
      }

      td {
        padding: 1rem;
        color: #444;
        vertical-align: middle;
      }
    }
  }
`;

const LeadInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const LeadAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #4285F4;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
`;

const LastUpdated = styled.span`
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.25rem;
  display: block;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #ddd;
  background: #fff;
  color: #666;
  position: relative;
  overflow: hidden;
  z-index: 1;

  &:hover {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  svg.spin {
    animation: ${spin} 1s linear infinite;
  }
`;

const TableLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #fff;
  border-top: 1px solid #eee;
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 6px;
  margin: 0 4px;
  border-radius: 50%;
  border: 1px solid #ddd;
  background-color: #ffffff;
  color: #333;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background-color: #f0f8ff;
    border-color: #007aff;
    color: #007aff;
    box-shadow: 0 0 4px rgba(0, 122, 255, 0.3);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background-color: #f9f9f9;
    color: #aaa;
  }
`;

const PageInfo = styled.span`
  font-size: 0.9rem;
  color: #666;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #4285F4;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: rgba(66, 133, 244, 0.1);
  }
`;

const LeadsTableSection = ({
  reallocateLeads,
  tableLoading,
  lastUpdated,
  isRefreshing,
  handleRefreshLeads,
  pagination,
  handlePageChange
}) => {
  const [selectedLead, setSelectedLead] = useState(null);

  const openLeadDetails = (lead) => {
    setSelectedLead(lead);
  };

  const closeModal = () => {
    setSelectedLead(null);
  };

  const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (e) {
    return '-';
  }
};

  return (
    <RecentRequestsSection>
      <SectionHeader>
        <div>
          <h2>Leads para Realocação</h2>
          <LastUpdated>
            {lastUpdated ? `Última atualização: ${lastUpdated.toLocaleTimeString()}` : 'Carregando...'}
          </LastUpdated>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <RefreshButton 
            onClick={handleRefreshLeads} 
            disabled={isRefreshing || tableLoading}
          >
            <FiRefreshCw className={isRefreshing ? 'spin' : ''} />
            Atualizar
          </RefreshButton>
        </div>
      </SectionHeader>
      
      <LeadsTableContainer>
        {tableLoading && (
          <TableLoadingOverlay>
            <Spinner />
          </TableLoadingOverlay>
        )}
        <RealocationLeadsTable>
          <thead>
            <tr>
              <th><FiUser /> Nome</th>
              <th><FiMail /> Email</th>
              <th><FiPhone /> Telefone</th>
              <th><FiMapPin /> Região</th>
              <th><FiCalendar /> Data de Realocação</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {reallocateLeads.length > 0 ? (
              reallocateLeads.map((lead, index) => (
                <tr key={index}>
                  <td>
                    <LeadInfo>
                      <LeadAvatar>
                        {lead.name?.charAt(0).toUpperCase() || 'L'}
                      </LeadAvatar>
                      {lead.name || 'Lead sem nome'}
                    </LeadInfo>
                  </td>
                  <td>{lead.email || '-'}</td>
                  <td>{lead.phone || '-'}</td>
                  <td>{lead.region || '-'}</td>
                  <td>{formatDate(lead.relocationDate)}</td>
                  <td>
                    <ActionButton onClick={() => openLeadDetails(lead)}>
                      <FiEye /> Ver
                    </ActionButton>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                  {tableLoading ? 'Carregando...' : 'Nenhum lead de realocação encontrado'}
                </td>
              </tr>
            )}
          </tbody>
        </RealocationLeadsTable>

        {pagination.totalItems > 0 && (
          <PaginationControls>
            <PaginationButton 
              onClick={(e) => handlePageChange(e, pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1 || tableLoading}
            >
              <FiChevronLeft />
            </PaginationButton>
            
            <PageInfo>
              Página {pagination.currentPage} de {pagination.totalPages}
            </PageInfo>
            
            <PaginationButton 
              onClick={(e) => handlePageChange(e, pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages || tableLoading}
            >
              <FiChevronRight />
            </PaginationButton>
          </PaginationControls>
        )}
      </LeadsTableContainer>

      <ModalLeadDetails lead={selectedLead} onClose={closeModal} />
    </RecentRequestsSection>
  );
};

export default LeadsTableSection;